import * as THREE from 'three';
import { Death } from '../entities/bosses/death';
import { EG } from '../entities/enemy-parts';
import { P } from '../world/player';
import { scene } from './scene';
/* ============================================================
   BOSS 在戰場上的渲染層
   ============================================================

   三件事，全部跑在**畫面幀**上：

   1. **每隻自己的 `upd(rig, o, dt)`。** 跟機體動畫同一個理由跑在畫面幀而不是
      模擬步上 —— 關節的平滑（`damp`、`angTo`）跑在固定 60Hz 上會被顯示率
      切成階梯。這裡不寫任何模擬狀態，只讀。
   2. **弱點標記。** 造型的 `upd()` 每幀覆寫發光強度，疊在造型上改不動，
      所以另外掛一圈環（`entities/boss-rig.ts` 建的），在這裡讓它呼吸。
   3. **死亡的四拍編排。** `entities/bosses/death.ts` 是移植產物（不要手改），
      這一支只做「戰場要怎麼用它」。

   ## 動畫通道 `o` 與小怪同一份規格（§6.2）

   造型檔因此不知道自己在圖鑑裡跑還是在戰場上跑。Boss 多用四個欄位：
   `stg`（階段序號）、`p2`（相容舊的兩階段補丁）、`broken`（部位被打掉）、
   `aim`（獨立追瞄的砲塔要瞄哪）。

   ## ⚠️ 死亡編排會把 rig 拆掉

   炸裂那一拍用 `Object3D.attach()` 把每個骨段攤平成 root 底下的自由剛體 ——
   那顆 root 之後**屬於死亡演出**，不能還給池子。`sim/fx.ts` 的交接點會
   立刻用 `rebuildBossRig()` 給池子裡那隻換一具新的身體。 */

var _o: any = {
  x: 0, z: 0, yaw: 0, aim: 0, t: 0, ph: 0, mv: 0, tel: 0, atk: 0, hit: 0,
  die: 0, p2: 0, stg: 0, broken: 0, sc: 1, q: {}, ry: 0, rp: 0, rr: 0, rz: 0
};

/* ---------- 護盾（召喚階段） ----------
   「Boss 進無敵、召一波、清完才破盾」要成立，**玩家得看得出來牠現在打不動**
   （設計文件 §3 第 1 點）。腳下一圈脈動的環是俯視鏡頭讀得最快的訊號。 */
var shieldRing: any = null;
function ensureShield() {
  if (shieldRing) return shieldRing;
  shieldRing = new THREE.Mesh(EG.tor, new THREE.MeshBasicMaterial({
    color: 0x9fd8ff, transparent: true, opacity: 0.6,
    depthWrite: false, blending: THREE.AdditiveBlending
  }));
  shieldRing.rotation.x = Math.PI / 2;
  shieldRing.renderOrder = 3;
  shieldRing.visible = false;
  scene.add(shieldRing);
  return shieldRing;
}

var lastT = 0;

/** 每個**畫面幀**呼叫一次，在 `interpolate()` 之後（位置要用內插後的）。 */
export function syncBossField(list, t) {
  var dt = Math.min(0.1, Math.max(0, t - lastT));
  lastT = t;
  var sr = ensureShield();
  sr.visible = false;
  for (var i = 0; i < list.length; i++) {
    var e = list[i];
    if (!e.T.bossRig || !e.rig) continue;
    driveRig(e, t, dt, 0);
    if (e.marks) {
      /* 還沒被打掉的弱點在呼吸。**破了就永遠不亮** —— 標記留在原地會讓
         玩家一直往一個沒有獎勵的位置繞。 */
      var k = 1 + 0.18 * Math.sin(t * 4.2);
      for (var m = 0; m < e.marks.length; m++) {
        var mk = e.marks[m];
        if (!mk.visible) continue;
        var p = e.T.parts[m];
        mk.scale.set(p.r * 2.4 * k, p.r * 2.4 * k, p.r * 0.9);
        mk.material.opacity = 0.55 + 0.35 * Math.sin(t * 4.2 + m);
      }
    }
    if (e.shield) {
      var rr = e.r * (1.25 + 0.06 * Math.sin(t * 5));
      sr.visible = true;
      sr.position.set(e.root.position.x, 0.16, e.root.position.z);
      sr.scale.set(rr, rr, e.r * 0.22);
      sr.material.opacity = 0.45 + 0.25 * Math.sin(t * 5);
    }
  }
  stepBossDeath(dt);
}

/** 把模擬狀態翻成動畫通道，跑一次該隻的 `upd()`。 */
function driveRig(e, t, dt, die) {
  var T = e.T, o = _o;
  o.x = e.root.position.x; o.z = e.root.position.z;
  o.yaw = e.face;
  o.aim = Math.atan2(P.pos.x - e.pos.x, P.pos.z - e.pos.z);
  o.t = t + e.tOff;
  o.ph = e.ph; o.mv = e.mv; o.tel = e.tel; o.atk = e.atk;
  o.hit = Math.min(1, e.flash * 6);
  o.die = die;
  o.stg = e.stg; o.p2 = e.stg > 0 ? 1 : 0;
  o.broken = e.broken || 0;
  o.sc = e.root.scale.x;
  o.ry = 0; o.rp = 0; o.rr = 0; o.rz = 0;
  if (T.upd) T.upd(e.rig, o, dt);
}

/* ---------- 死亡：規格 §9 的四拍 + 骨段解體 ---------- */

/** 演完之後殘骸留多久。原型的 `D_END` 是 5.60 —— 戰場上照抄，
    因為那正是「打完一隻 Boss 之後喘口氣」該有的長度。 */
var DEATH_END = 5.6;
var dying: any = null;

/** 接管一具 rig 開始演死亡。**呼叫端接著要 `rebuildBossRig()`** ——
    這顆 root 從現在起屬於死亡演出，不能再回池子。 */
export function armBossDeath(e) {
  /* 上一隻還沒演完就先收掉。Boss 相隔三分鐘以上，正常玩不會撞到；
     撞到的話留兩份殘骸在場上比較糟（而且 `Death` 是單例，狀態會打架）。 */
  if (dying) endBossDeath();
  var root = e.root, T = e.T;
  root.userData.sc0 = root.scale.x;
  root.userData.px = root.position.x;
  root.userData.pz = root.position.z;
  Death.arm(root, T);
  dying = { root: root, T: T, rig: e.rig, face: e.face, tOff: e.tOff, t: 0 };
}

function endBossDeath() {
  if (!dying) return;
  if (dying.root.parent) dying.root.parent.remove(dying.root);
  Death.reset();
  dying = null;
}

function stepBossDeath(dt) {
  if (!dying) return;
  dying.t += dt;
  var scale = Death.step(dt, dying.root);
  /* **炸裂之前 rig 還要繼續跑正常動畫** —— 抖動與吸氣是疊在上面的，
     不是取代它（原型的 `if (!Death.burst) upd(...)`）。 */
  if (!Death.burst && dying.T.upd) {
    var o = _o;
    o.x = dying.root.position.x; o.z = dying.root.position.z;
    o.yaw = dying.face; o.aim = dying.face;
    o.t = dying.t + dying.tOff;
    o.mv = 0; o.tel = 0; o.atk = 0; o.hit = 0; o.die = 0;
    o.stg = 0; o.p2 = 0; o.broken = 0; o.sc = dying.root.scale.x;
    dying.T.upd(dying.rig, o, dt * scale);
  }
  if (dying.t >= DEATH_END) endBossDeath();
}

/** 開新的一場要收乾淨 —— 不收的話上一場的殘骸與爆炸環會出現在新場地上。 */
export function clearBossField() {
  endBossDeath();
  if (shieldRing) shieldRing.visible = false;
}
